import React, { useState, useEffect, Fragment } from "react";
import { Card, CardSection } from "cxa-components";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import {
  formatMoney,
  getProductPrice,
  calculateUtilization,
  isDefaultPlan,
} from "./EnrollmentDataUtil";
import UpArrow from "../../common/ArrowUp";
import DownArrow from "../../common/ArrowDown";
import SelectedIcon from "../../../assets/images/svg/selected_icon.svg";
import InformationIcon from "../../../assets/images/svg/information_icon.svg";
import messages from "components/messagesDescription";
import withMatchMediaProps from "@hocs/with-match-media-props";

const IntlContext = React.createContext();
const ScreenContext = React.createContext();

const TableRow = ({ description, value }) => {
  return (
    <tr style={{ lineHeight: "36px" }}>
      <td>
        <span style={{ width: "25px" }}></span>
      </td>
      <td>{description}:</td>
      <td style={{ textAlign: "right" }}>
        <strong className="balance-style">{formatMoney(value)}</strong>
      </td>
    </tr>
  );
};

TableRow.propTypes = {
  description: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
};

const useEnrollmentData = () => {
  const enrollmentData = useSelector(state => state.enrollment.details);
  const {
    planBasedConvertLeaveBenefits,
    sumAssuredBasedBenefits,
    planBasedBenefits,
    planBasedOptionalBenefits,
    priceTagMode,
  } = enrollmentData;

  const [entitlementPoints, utilizationPoints] = calculateUtilization(enrollmentData);

  const entitledPoints =
    entitlementPoints +
    Number(enrollmentData.accounts.totalEntitlement.numericValue) -
    Number(enrollmentData.accounts.totalUtilization.numericValue);
  const utilizedPoints = utilizationPoints;
  const availablePoints =
    entitledPoints - utilizedPoints >= 0 ? entitledPoints - utilizedPoints : 0;
  const overPurchasePoints = utilizedPoints > entitledPoints ? utilizedPoints - entitledPoints : 0;
  const completedPercentage =
    utilizedPoints <= entitledPoints
      ? (utilizedPoints / entitledPoints) * 100
      : (overPurchasePoints / utilizedPoints) * 100;
  const openingBalance =
    Number(enrollmentData.accounts.totalEntitlement.numericValue) -
    Number(enrollmentData.accounts.totalUtilization.numericValue);
  return {
    entitledPoints,
    utilizedPoints,
    availablePoints,
    overPurchasePoints,
    completedPercentage,
    planBasedConvertLeaveBenefits,
    sumAssuredBasedBenefits,
    planBasedBenefits,
    planBasedOptionalBenefits,
    priceTagMode,
    openingBalance,
  };
};

const ProductPrice = ({ benefits }) => {
  const { priceTagMode } = useEnrollmentData();
  const intl = React.useContext(IntlContext);
  return (
    Array.isArray(benefits) &&
    benefits.length > 0 &&
    benefits.map(benefit => {
      const productPrice = getProductPrice(benefit, priceTagMode);
      const displayPrice = formatMoney(0 - productPrice);
      return (
        benefit.selectable == "true" && (
          <tr key={benefit.benefitID}>
            <td>
              {!isDefaultPlan(benefit) ? (
                <SelectedIcon className="icon" />
              ) : (
                <span style={{ width: "25px" }}></span>
              )}
            </td>
            <td className="product-price-container">
              <span>
                {intl.formatMessage({
                  id: benefit.benefitName,
                  defaultMessage: benefit.benefitName,
                })}
              </span>
            </td>
            <td style={{ textAlign: "right" }}>
              <strong>{displayPrice}</strong>
            </td>
          </tr>
        )
      );
    })
  );
};

const BenefitSelectionDetails = () => {
  const {
    entitledPoints,
    utilizedPoints,
    availablePoints,
    overPurchasePoints,
    planBasedConvertLeaveBenefits,
    sumAssuredBasedBenefits,
    planBasedBenefits,
    planBasedOptionalBenefits,
    openingBalance,
    priceTagMode,
  } = useEnrollmentData();

  const intl = React.useContext(IntlContext);
  let creditBenefits = [];
  let debitBenefits = [];
  [
    ...planBasedConvertLeaveBenefits,
    ...sumAssuredBasedBenefits,
    ...planBasedBenefits,
    ...planBasedOptionalBenefits,
  ].forEach(benefit => {
    if (getProductPrice(benefit, priceTagMode) < 0) {
      creditBenefits.push(benefit);
    } else {
      debitBenefits.push(benefit);
    }
  });
  return (
    <div className="benefit-selection-container">
      <table>
        <TableRow
          description={intl.formatMessage(messages.entitlementDescription)}
          value={entitledPoints}
        />
        <tr style={{ lineHeight: "36px" }}>
          <td>
            <span style={{ width: "25px" }}></span>
          </td>
          <td style={{ paddingLeft: "15px" }}>
            {intl.formatMessage(messages.enrollment.openingBalanceDescription)}:
          </td>
          <td style={{ textAlign: "right" }}>
            <strong className="balance-style">{formatMoney(openingBalance)}</strong>
          </td>
        </tr>
        <ProductPrice benefits={creditBenefits} />
        <TableRow
          description={intl.formatMessage(messages.enrollment.utilizationDescription)}
          value={0 - utilizedPoints}
        />
        <ProductPrice benefits={debitBenefits} />
        <TableRow
          description={intl.formatMessage(messages.enrollment.remainingBalanceDescription)}
          value={availablePoints}
        />
      </table>
      {overPurchasePoints > 0 && (
        <div className="note-style">
          <span style={{ width: "25px", marginRight: "10px" }}>
            <InformationIcon className="icon" />
          </span>
          <div
            dangerouslySetInnerHTML={{
              __html: intl.formatMessage(messages.enrollment.overPurchaseNote, {
                overPurchased: formatMoney(overPurchasePoints),
              }),
            }}
          ></div>
        </div>
      )}
    </div>
  );
};

const getWalletContainerStyle = (isSticky, isNarrowScreen) => {
  if (!isSticky) return {};

  return {
    position: "fixed",
    width: "100vw",
    top: isNarrowScreen ? "4rem" : "6rem",
    zIndex: 10,
    left: "0",
    backgroundColor: "#FFF",
    boxShadow: "0 4px 4px -2px rgba(0, 0, 0, 0.5)",
    padding: "0.5rem 6vw",
  };
};

const WalletLabel = ({ toggleShowMore, showMore }) => {
  const intl = React.useContext(IntlContext);
  return (
    <Fragment>
      <span className="wallet-label">
        <strong>{intl.formatMessage(messages.enrollment.yourSelectionDescription)}</strong>
      </span>
      <button className="link" onClick={toggleShowMore}>
        <span style={{ position: "relative", left: "10px" }}>
          {showMore ? (
            <UpArrow width="14px" height="14px" />
          ) : (
            <DownArrow width="16px" height="16px" />
          )}
        </span>
      </button>
    </Fragment>
  );
};

WalletLabel.propTypes = {
  toggleShowMore: PropTypes.func.isRequired,
  showMore: PropTypes.bool.isRequired,
};

const WalletAvailable = () => {
  const intl = React.useContext(IntlContext);
  const { availablePoints, entitledPoints } = useEnrollmentData();
  return (
    <div className="wallet-available">
      <span>{intl.formatMessage(messages.enrollment.availableBalanceDescription)}: </span>
      <strong>{formatMoney(availablePoints)}</strong>
      <span>{intl.formatMessage(messages.ofDescription)} </span>
      <strong>
        <strong>{formatMoney(entitledPoints)}</strong>
      </strong>
    </div>
  );
};

const WalletBarContainer = () => {
  const { entitledPoints, utilizedPoints, completedPercentage } = useEnrollmentData();

  return (
    <div
      className={
        utilizedPoints <= entitledPoints
          ? "wallet-bar-container"
          : "wallet-over-purchased-bar-container"
      }
    >
      <div
        className={utilizedPoints <= entitledPoints ? "wallet-filler" : "wallet-over-purchased"}
        style={{ width: `${completedPercentage}%` }}
      ></div>
    </div>
  );
};

const UtilizationContainer = () => {
  const intl = React.useContext(IntlContext);
  const { utilizedPoints, overPurchasePoints, entitledPoints } = useEnrollmentData();

  return (
    <div className="utilization-container">
      <span>{intl.formatMessage(messages.enrollment.usedDescription)}: </span>
      <span className="used-balance">
        <strong>{formatMoney(overPurchasePoints > 0 ? entitledPoints : utilizedPoints)}</strong>
      </span>
      {overPurchasePoints > 0 && (
        <span>
          <span> + {intl.formatMessage(messages.enrollment.overPurchasedDescription)}: </span>
          <span className="over-purchased-balance">
            <strong>{formatMoney(overPurchasePoints)}</strong>
          </span>
        </span>
      )}
    </div>
  );
};

const WalletComponent = () => {
  const [showMore, setShowMore] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const isNarrowScreen = React.useContext(ScreenContext);

  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 200);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const toggleShowMore = e => {
    e.preventDefault();
    setShowMore(!showMore);
  };

  return (
    <div className="wallet-container">
      <div style={getWalletContainerStyle(isSticky, isNarrowScreen)}>
        {!isSticky && <WalletLabel toggleShowMore={toggleShowMore} showMore={showMore} />}
        <WalletAvailable />
        <WalletBarContainer />
        <UtilizationContainer />
      </div>

      {showMore ? <BenefitSelectionDetails /> : ""}
    </div>
  );
};

const WalletPP = ({ intl, isNarrowScreen }) => {
  return (
    <IntlContext.Provider value={intl}>
      <ScreenContext.Provider value={isNarrowScreen}>
        <Card className="enrollment-card">
          <CardSection>
            {/* <BalanceProgressBar accountInfo={accounts} completed="60" /> */}
            <WalletComponent />
          </CardSection>
        </Card>
      </ScreenContext.Provider>
    </IntlContext.Provider>
  );
};

WalletPP.propTypes = {
  intl: PropTypes.func.isRequired,
  isNarrowScreen: PropTypes.bool.isRequired,
};

export default withMatchMediaProps({
  isNarrowScreen: {
    maxWidth: 680,
  },
})(WalletPP);



// WEBPACK FOOTER //
// ./components/employee/shop/WalletPP.js